import type { GameEvent } from '../schema';

export const INVESTMENT_EVENTS: GameEvent[] = [
  {
    id: 'investment.firstAllocation',
    title: 'Where does it go?',
    description:
      'You have money that is not needed this year. Every option trades certainty against expected return.',
    category: 'investment',
    pools: ['pool.investment'],
    weight: 8,
    cooldown: 3,
    requires: [{ k: 'num', path: 'cash', op: '>=', value: 8000 }],
    choices: [
      {
        id: 'conservative',
        label: 'Something safe',
        detail: 'Low return, nearly no risk, always accessible.',
        hint: 'safe',
        tags: ['security'],
        outcomes: [
          {
            weight: 1,
            label: 'Bought conservative assets',
            narrative: 'It will barely beat inflation, and you will never lose sleep over it.',
            tone: 'neutral',
            effects: [
              { k: 'invest', op: 'buy', assetId: 'conservative', amount: 6000 },
              { k: 'habit', habit: 'investing', delta: 6 },
            ],
          },
        ],
      },
      {
        id: 'balanced',
        label: 'A broad mix',
        detail: 'Diversified. Dull. Historically effective.',
        hint: 'safe',
        tags: ['finance', 'patience'],
        outcomes: [
          {
            weight: 1,
            label: 'Bought a balanced portfolio',
            narrative: 'The boring answer, which is usually the right one.',
            tone: 'positive',
            effects: [
              { k: 'invest', op: 'buy', assetId: 'balanced', amount: 7000 },
              { k: 'habit', habit: 'investing', delta: 9 },
            ],
          },
        ],
      },
      {
        id: 'growth',
        label: 'Equity-heavy growth',
        detail: 'Higher expected return. Real drawdowns.',
        hint: 'mixed',
        tags: ['finance', 'risk'],
        outcomes: [
          {
            weight: 1,
            label: 'Bought growth assets',
            narrative: 'Over thirty years this probably wins. Over any three, who knows.',
            tone: 'neutral',
            effects: [
              { k: 'invest', op: 'buy', assetId: 'growth', amount: 7000 },
              { k: 'habit', habit: 'investing', delta: 9 },
            ],
          },
        ],
      },
      {
        id: 'wait',
        label: 'Keep it in cash for now',
        hint: 'safe',
        defers: { id: 'int.invest', label: 'Start investing', topic: 'finance', urgency: 16, decayEventId: 'investment.cashDrag' },
        outcomes: [
          {
            weight: 1,
            label: 'Stayed in cash',
            narrative: 'It feels like a neutral choice. It is not; inflation is a decision made for you.',
            tone: 'neutral',
            effects: [{ k: 'inertia', op: 'pressure', delta: 4 }],
          },
        ],
      },
    ],
  },
  {
    id: 'investment.cashDrag',
    title: 'Ten years in cash',
    description: 'The money you meant to invest sat in a current account for a decade and quietly shrank.',
    category: 'investment',
    pools: ['pool.inertia'],
    weight: 0,
    automatic: true,
    choices: [
      {
        id: 'ack',
        label: 'Notice the cost',
        outcomes: [
          {
            weight: 1,
            label: 'Lost a decade of compounding',
            narrative: 'Nothing went wrong. That was the problem.',
            tone: 'negative',
            effects: [
              { k: 'regret', op: 'add', id: 'regret.invest', label: 'Left money in cash for a decade', weight: 16 },
              { k: 'habit', habit: 'investing', delta: -6 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'investment.hotTip',
    title: 'Everyone is making money on this',
    description:
      'A colleague has tripled their money in eight months and cannot stop talking about it. The mechanism is unclear.',
    category: 'investment',
    pools: ['pool.investment', 'pool.luck'],
    weight: 6,
    cooldown: 6,
    requires: [{ k: 'num', path: 'cash', op: '>=', value: 5000 }],
    weightMods: [
      { conditions: [{ k: 'str', path: 'marketRegime', op: '==', value: 'boom' }], multiplier: 2.6 },
      { conditions: [{ k: 'trait', trait: 'impulsiveness', op: '>=', value: 60 }], multiplier: 1.5 },
    ],
    choices: [
      {
        id: 'all-in',
        label: 'Put real money in',
        hint: 'risky',
        tags: ['risk', 'greed'],
        cost: [{ k: 'invest', op: 'buy', assetId: 'speculative', amount: 18000 }],
        outcomes: [
          {
            weight: 22,
            label: 'It ran',
            narrative: 'It went up absurdly and you sold near the top, which is mostly luck and entirely yours.',
            tone: 'positive',
            favouredBy: [{ conditions: [{ k: 'str', path: 'marketRegime', op: '==', value: 'boom' }], multiplier: 1.6 }],
            effects: [
              { k: 'invest', op: 'shock', assetId: 'speculative', fraction: 1.8 },
              { k: 'stat', stat: 'happiness', delta: 10 },
              { k: 'trait', trait: 'riskTolerance', delta: 8 },
              { k: 'counter', name: 'luckyBreaks', delta: 1 },
            ],
          },
          {
            weight: 30,
            label: 'Sideways, then nothing',
            narrative: 'It drifted, you held, and eventually sold for roughly what you paid.',
            tone: 'neutral',
            effects: [{ k: 'invest', op: 'shock', assetId: 'speculative', fraction: -0.08 }],
          },
          {
            weight: 48,
            label: 'It collapsed',
            narrative: 'Down eighty per cent in three weeks. The colleague has stopped mentioning it.',
            tone: 'negative',
            effects: [
              { k: 'invest', op: 'shock', assetId: 'speculative', fraction: -0.78 },
              { k: 'stat', stat: 'stress', delta: 12 },
              { k: 'trait', trait: 'riskTolerance', delta: -10 },
              { k: 'skill', skill: 'financial', delta: 5 },
              { k: 'memory', title: 'An expensive lesson in speculation', tone: 'negative', category: 'investment', weight: 5 },
            ],
          },
        ],
      },
      {
        id: 'small',
        label: 'A small amount, for curiosity',
        hint: 'mixed',
        tags: ['risk'],
        cost: [{ k: 'invest', op: 'buy', assetId: 'speculative', amount: 2500 }],
        outcomes: [
          {
            weight: 30,
            label: 'A nice win',
            narrative: 'Small money, decent return, no drama.',
            tone: 'positive',
            effects: [{ k: 'invest', op: 'shock', assetId: 'speculative', fraction: 1.2 }, { k: 'skill', skill: 'financial', delta: 3 }],
          },
          {
            weight: 70,
            label: 'Gone',
            narrative: 'You lost it and learned the same lesson for a tenth of the price.',
            tone: 'neutral',
            effects: [{ k: 'invest', op: 'shock', assetId: 'speculative', fraction: -0.85 }, { k: 'skill', skill: 'financial', delta: 4 }],
          },
        ],
      },
      {
        id: 'pass',
        label: 'Pass',
        hint: 'safe',
        tags: ['discipline'],
        outcomes: [
          {
            weight: 1,
            label: 'Stayed out',
            narrative: 'You will hear about the one that worked and never about the nine that did not.',
            tone: 'neutral',
            effects: [{ k: 'trait', trait: 'patience', delta: 3 }],
          },
        ],
      },
    ],
  },
  {
    id: 'investment.crashPanic',
    title: 'The portfolio is down thirty per cent',
    description:
      'Everything is red. The news has a special graphic for it. Selling would end the feeling immediately.',
    category: 'investment',
    pools: ['pool.investment', 'pool.crisis'],
    weight: 8,
    cooldown: 5,
    requires: [
      { k: 'num', path: 'investments', op: '>=', value: 15000 },
      { k: 'any', of: [{ k: 'str', path: 'marketRegime', op: '==', value: 'bust' }, { k: 'str', path: 'marketRegime', op: '==', value: 'soft' }] },
    ],
    choices: [
      {
        id: 'hold',
        label: 'Do nothing',
        hint: 'mixed',
        tags: ['patience', 'discipline'],
        outcomes: [
          {
            weight: 1,
            label: 'Held',
            narrative: 'The hardest available action. It is also, historically, the correct one.',
            tone: 'neutral',
            effects: [
              { k: 'stat', stat: 'stress', delta: 10 },
              { k: 'trait', trait: 'patience', delta: 6 },
              { k: 'skill', skill: 'financial', delta: 5 },
              { k: 'schedule', afterYears: 4, label: 'The recovery', effects: [{ k: 'invest', op: 'shock', assetId: 'growth', fraction: 0.22 }, { k: 'stat', stat: 'happiness', delta: 6 }] },
            ],
          },
        ],
      },
      {
        id: 'buy',
        label: 'Buy more',
        hint: 'risky',
        requires: [{ k: 'num', path: 'cash', op: '>=', value: 8000 }],
        showWhenLocked: true,
        tags: ['risk', 'discipline'],
        cost: [{ k: 'invest', op: 'buy', assetId: 'growth', amount: 8000 }],
        outcomes: [
          {
            weight: 68,
            label: 'Bought the dip',
            narrative: 'Deeply uncomfortable and, four years later, obviously right.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'stress', delta: 12 },
              { k: 'schedule', afterYears: 4, label: 'The rebound', effects: [{ k: 'invest', op: 'shock', assetId: 'growth', fraction: 0.38 }, { k: 'stat', stat: 'happiness', delta: 9 }] },
            ],
          },
          {
            weight: 32,
            label: 'It kept falling',
            narrative: 'You caught the knife. It fell another thirty per cent before it stopped.',
            tone: 'negative',
            effects: [
              { k: 'invest', op: 'shock', assetId: 'growth', fraction: -0.28 },
              { k: 'stat', stat: 'stress', delta: 16 },
              { k: 'trait', trait: 'resilience', delta: 5 },
            ],
          },
        ],
      },
      {
        id: 'sell',
        label: 'Sell and stop the pain',
        hint: 'costly',
        tags: ['fear'],
        outcomes: [
          {
            weight: 1,
            label: 'Sold at the bottom',
            narrative: 'The anxiety stopped instantly. So did the recovery you would have participated in.',
            tone: 'negative',
            effects: [
              { k: 'invest', op: 'sell', assetId: 'growth', amount: 0 },
              { k: 'invest', op: 'sell', assetId: 'speculative', amount: 0 },
              { k: 'stat', stat: 'stress', delta: -12 },
              { k: 'habit', habit: 'investing', delta: -12 },
              { k: 'regret', op: 'add', id: 'regret.panicSold', label: 'Sold everything in a crash', weight: 16 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'investment.diversifyNudge',
    title: 'It is all in one thing',
    description: 'A single position has grown to most of your portfolio. It has been wonderful so far.',
    category: 'investment',
    pools: ['pool.investment'],
    weight: 5,
    cooldown: 6,
    requires: [{ k: 'num', path: 'holding.speculative', op: '>=', value: 25000 }],
    choices: [
      {
        id: 'rebalance',
        label: 'Take profits and diversify',
        hint: 'safe',
        tags: ['discipline', 'finance'],
        outcomes: [
          {
            weight: 1,
            label: 'Rebalanced',
            narrative: 'You sold your best performer, which always feels like a mistake and rarely is.',
            tone: 'positive',
            effects: [
              { k: 'invest', op: 'sell', assetId: 'speculative', amount: 20000 },
              { k: 'invest', op: 'buy', assetId: 'balanced', amount: 18000 },
              { k: 'skill', skill: 'financial', delta: 6 },
              { k: 'stat', stat: 'stress', delta: -4 },
            ],
          },
        ],
      },
      {
        id: 'ride',
        label: 'Let the winner run',
        hint: 'risky',
        tags: ['risk'],
        outcomes: [
          {
            weight: 44,
            label: 'It kept running',
            narrative: 'Concentration built the wealth. It was also always going to be the thing that threatened it.',
            tone: 'positive',
            effects: [{ k: 'invest', op: 'shock', assetId: 'speculative', fraction: 0.55 }, { k: 'trait', trait: 'riskTolerance', delta: 5 }],
          },
          {
            weight: 56,
            label: 'It gave it all back',
            narrative: 'Two bad quarters erased four good years.',
            tone: 'negative',
            effects: [
              { k: 'invest', op: 'shock', assetId: 'speculative', fraction: -0.62 },
              { k: 'stat', stat: 'stress', delta: 14 },
              { k: 'skill', skill: 'financial', delta: 6 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'investment.pensionMatch',
    title: 'Free money, conditionally',
    description: 'Your employer will match pension contributions up to a limit. It requires giving up salary today.',
    category: 'investment',
    pools: ['pool.investment', 'pool.finance.early'],
    weight: 7,
    unique: true,
    requires: [{ k: 'careerStatus', anyOf: ['employed'] }, { k: 'num', path: 'salary', op: '>=', value: 35000 }],
    choices: [
      {
        id: 'max',
        label: 'Contribute the maximum',
        hint: 'safe',
        tags: ['discipline', 'finance'],
        outcomes: [
          {
            weight: 1,
            label: 'Maxed the match',
            narrative: 'A guaranteed hundred per cent return on the matched portion. There is no better trade available.',
            tone: 'positive',
            effects: [
              { k: 'flag', flag: 'pensionMaxed', value: true },
              { k: 'habit', habit: 'investing', delta: 12 },
              { k: 'retirement', mode: 'add', value: 4000 },
              { k: 'skill', skill: 'financial', delta: 5 },
            ],
          },
        ],
      },
      {
        id: 'partial',
        label: 'Contribute a little',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'Contributed partially',
            narrative: 'Something is better than nothing, and less than the free money on the table.',
            tone: 'neutral',
            effects: [
              { k: 'habit', habit: 'investing', delta: 5 },
              { k: 'retirement', mode: 'add', value: 1200 },
            ],
          },
        ],
      },
      {
        id: 'none',
        label: 'Keep the salary',
        hint: 'risky',
        outcomes: [
          {
            weight: 1,
            label: 'Opted out',
            narrative: 'You needed the money now. That was true, and it was still expensive.',
            tone: 'negative',
            effects: [{ k: 'counter', name: 'missedOpportunities', delta: 1 }],
          },
        ],
      },
    ],
  },
];
